import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { deleteLegalEntityControl, getLegalEntityControl, insertLegalEntityControl, updateLegalEntityControl } from 'src/store/slices/dynamicCatalogSlice';
import AlertAndLoading from 'src/components/AlertAndLoading';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  inputSx,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';

function LegalEntityControlDynamic() {
  const classes = useDynamicStyles();
  const dispatch = useDispatch();
  const [legalControl, setLegalControl] = React.useState([]);
  const [editPopUp, setEditPopUp] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");
  const [wordText, setWordText] = React.useState("");
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [responseMessage, setresponseMessage] = React.useState('');
  const [uyari, setUyari] = React.useState(false);
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });
  const [searchText, setSearchText] = React.useState('');

  const isUpdate = !!selectedId;

  const loadLegalControl = () => {
    setLoading(true);
    dispatch(getLegalEntityControl()).then(response => {
      // Son kelime silindiğinde liste boş dönebilir; başarılı cevapta her zaman güncellenir
      if (response.meta?.requestStatus === 'fulfilled') {
        setLegalControl(Array.isArray(response.payload) ? response.payload : []);
      }
      setLoading(false);
    })
  };

  useEffect(() => {
    loadLegalControl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  function InfoAlert(ErrorDescription, ErrorType, ErrorTitle) {
    setresponseMessage(ErrorDescription);
    setAlertType({
      ErrorType: ErrorType,
      ErrorTitle: ErrorTitle
    })
    setUyari(true);
    return
  }

  function openInsert() {
    setSelectedId("");
    setWordText("");
    setEditPopUp(true);
  }

  function openUpdate(row) {
    setSelectedId(row.ID);
    setWordText(row.ACIKLAMA);
    setEditPopUp(true);
  }

  function closeEditPopUp() {
    if (saving) return;
    setEditPopUp(false);
  }

  function closeDeletePopUp() {
    if (saving) return;
    setDeleteTarget(null);
  }

  function deleteLegalEntityCont() {
    if (!deleteTarget) return;
    setSaving(true);
    dispatch(deleteLegalEntityControl(deleteTarget.ID)).then(() => {
      setSaving(false);
      setDeleteTarget(null);
      loadLegalControl();
    })
  }

  function saveLegalEntityCont() {
    if (!wordText?.trim()) {
      InfoAlert('Lütfen Kutucukları Boş Bırakmayınız!...', 'error', 'Hata');
      return
    }
    setSaving(true);
    const request = isUpdate
      ? dispatch(updateLegalEntityControl([wordText, selectedId]))
      : dispatch(insertLegalEntityControl(wordText));
    request.then(() => {
      setSaving(false);
      setEditPopUp(false);
      setSelectedId("");
      setWordText("");
      loadLegalControl();
    })
  }

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const rowCount = legalControl?.length || 0;
  const filteredRows = useMemo(() => filterDynamicRows(legalControl, {
    searchText,
    searchFields: ['ACIKLAMA']
  }), [legalControl, searchText]);

  return (
    <>
      <AlertAndLoading Message={responseMessage} open={uyari} AlertType={alertType} onClose={uyariKapat} />
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Tüzel Kişi Kontrol Kelimeleri"
          subheader={loading ? 'Yükleniyor…' : `${rowCount} kelime`}
          action={
            <Button startIcon={<AddIcon />} sx={darkButtonSx} onClick={openInsert}>
              Yeni Kelime
            </Button>
          }
        />
        {loading && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { backgroundColor: ORANGE }, backgroundColor: '#ffe0b2' }} />}
        <DynamicListToolbar
          searchText={searchText}
          onSearchChange={setSearchText}
          placeholder="Kelime ara"
          shownCount={filteredRows.length}
        />
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Kelime</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.ID}>
                  <TableCell align="left">{row.ACIKLAMA}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Düzenle">
                      <IconButton size="small" onClick={() => openUpdate(row)}>
                        <EditIcon sx={{ fontSize: 18, color: ORANGE }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton size="small" onClick={() => setDeleteTarget(row)}>
                        <DeleteOutlineIcon sx={{ fontSize: 18, color: '#e53935' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className={classes.emptyState}>
                    {rowCount === 0 ? 'Kayıtlı kelime bulunamadı.' : 'Aramaya uygun kayıt bulunamadı.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Ekle / Güncelle */}
      <Dialog open={editPopUp} onClose={closeEditPopUp} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          {isUpdate ? 'Kelime Düzenle' : 'Yeni Kelime'}
          <IconButton size="small" onClick={closeEditPopUp} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          <TextField
            label="Kelime"
            autoFocus
            fullWidth
            size="small"
            sx={inputSx}
            value={wordText}
            onChange={(e) => setWordText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && wordText?.trim()) saveLegalEntityCont(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeEditPopUp} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={saveLegalEntityCont}
            disabled={saving || !wordText?.trim()}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            {isUpdate ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={closeDeletePopUp} fullWidth maxWidth="xs" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          Kelime Sil
          <IconButton size="small" onClick={closeDeletePopUp} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: ORANGE, mt: '2px' }} />
            <Box sx={{ fontSize: '13px', color: DARK }}>
              <b>"{deleteTarget?.ACIKLAMA}"</b> kelimesi silinecek. Emin misiniz?
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeDeletePopUp} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 90, color: '#fff', backgroundColor: '#e53935', '&:hover': { backgroundColor: '#c62828' } }}
            onClick={deleteLegalEntityCont}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LegalEntityControlDynamic;
